import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CopyButton } from './CopyButton';

type JsonBlockProps = {
  /** Raw string content to display (will attempt JSON pretty-printing) */
  content: string;
  /** Language for syntax highlighting (default: json) */
  language?: string;
  /** Maximum height in CSS units (default: 20rem) */
  maxHeight?: string;
  /** Label used by the copy-to-clipboard toast (e.g. "request body") */
  copyLabel?: string;
  /** Hide the copy-to-clipboard button (default: false) */
  hideCopy?: boolean;
};

/**
 * Dark-themed code block with syntax highlighting, suitable for
 * displaying JSON payloads, headers, and other structured text.
 */
export const JsonBlock: React.FC<JsonBlockProps> = ({
  content,
  language = 'json',
  maxHeight = '20rem',
  copyLabel,
  hideCopy = false,
}) => {
  const formatted = React.useMemo(() => {
    if (language !== 'json') return content;
    try {
      return JSON.stringify(JSON.parse(content), null, 2);
    } catch {
      return content;
    }
  }, [content, language]);

  return (
    <div className="relative group/json">
      {!hideCopy && formatted && <CopyButton value={formatted} label={copyLabel} />}
      <div
        className="rounded-md border border-slate-800 bg-slate-950 overflow-auto"
        style={{ maxHeight }}
      >
        <SyntaxHighlighter
          language={language}
          style={vscDarkPlus}
          customStyle={{ margin: 0, background: 'transparent', fontSize: '12px' }}
          wrapLongLines
        >
          {formatted}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};
